import * as React from 'react'

import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
  Controller,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'

import { Render } from '@/components/render-if'
import { Label } from '@/components/ui/label'
import { cn } from '@/infra'

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TTransformedValues = TFieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName, TTransformedValues>) => {
  const values = React.useMemo(() => ({ name: props.name }), [props.name])
  return (
    <FormFieldContext.Provider value={values}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue)

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId()
  const value = React.useMemo(() => ({ id }), [id])
  return (
    <FormItemContext.Provider value={value}>
      <div
        data-slot="form-item"
        className={cn('font-nunito grid [&>*:nth-child(1)]:mb-2 [&>*:nth-child(2)]:mb-1', className)}
        {...props}
      />
    </FormItemContext.Provider>
  )
}

function FormLabel({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField()
  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      aria-disabled={props['aria-disabled']}
      className={cn('font-nunito data-[error=true]:text-destructive aria-[disabled=true]:text-neutral-500', className)}
      htmlFor={formItemId}
      {...props}
    />
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : `${formDescriptionId}`}
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField()

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

const formMessageSizeMap = {
  sm: 'h-4',
  md: 'h-6',
  lg: 'h-6 md:h-8',
}

function FormMessage({
  className,
  helperText,
  size = 'lg',
  ...props
}: React.ComponentProps<'p'> & { helperText?: string; size?: 'sm' | 'md' | 'lg' }) {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message ?? '') : props.children

  return (
    <div className={cn(formMessageSizeMap[size], className)}>
      <Render.If condition={body}>
        {() => (
          <p role="alert" data-slot="form-message" id={formMessageId} className="text-destructive text-xs" {...props}>
            {body}
          </p>
        )}
      </Render.If>
      <Render.If condition={helperText}>
        {() => (
          <p role="caption" data-slot="form-helper-text" className={cn('text-xs text-neutral-600', body && 'mt-2')}>
            {helperText}
          </p>
        )}
      </Render.If>
    </div>
  )
}

export { useFormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField }
